/**
 * Theme tokens for inline styles.
 *
 * Pack / lobby / quiz UIs use `cssTheme` → `--color-primary|secondary|tertiary`
 * (pack `[data-theme]` defaults; premium branding may override).
 *
 * Party / game-specific dark screens use `makeGameTheme(...)` so **free rooms keep
 * their original hex look**, while premium branding overrides via `--brand-*`
 * (set only when the host saves palette colors).
 */

export type BrandSlot = 'primary' | 'secondary' | 'tertiary';

export type GameThemeDefaults = {
  primary: string;
  primaryRgb: string;
  secondary?: string;
  secondaryRgb?: string;
  /** Maps to brand tertiary / UI accent */
  tertiary?: string;
  tertiaryRgb?: string;
};

/** Pack-token theme (lobby, quiz host/player, standings defaults). */
export const cssTheme = {
  primary: 'var(--color-primary)',
  primaryLight: 'rgba(var(--color-primary-rgb), 0.15)',
  primaryBorder: 'rgba(var(--color-primary-rgb), 0.3)',
  primaryStrong: 'rgba(var(--color-primary-rgb), 0.5)',

  secondary: 'var(--color-secondary)',
  secondaryLight: 'rgba(var(--color-secondary-rgb), 0.15)',
  secondaryBorder: 'rgba(var(--color-secondary-rgb), 0.3)',

  tertiary: 'var(--color-tertiary)',
  tertiaryLight: 'rgba(var(--color-tertiary-rgb), 0.15)',
  tertiaryBorder: 'rgba(var(--color-tertiary-rgb), 0.3)',

  accent: 'var(--color-tertiary)',
  accentLight: 'rgba(var(--color-tertiary-rgb), 0.15)',
  accentBorder: 'rgba(var(--color-tertiary-rgb), 0.3)',

  success: 'var(--color-success)',
  successLight: 'rgba(var(--color-success-rgb), 0.15)',
  successBorder: 'rgba(var(--color-success-rgb), 0.3)',

  error: 'var(--color-error)',
  errorLight: 'rgba(var(--color-error-rgb), 0.15)',
  errorBorder: 'rgba(var(--color-error-rgb), 0.3)',

  warning: 'var(--color-warning)',
  warningLight: 'rgba(var(--color-warning-rgb), 0.15)',
  warningBorder: 'rgba(var(--color-warning-rgb), 0.3)',

  textMuted: 'rgba(255, 255, 255, 0.6)',
} as const;

/**
 * Build a game palette: free rooms use `defaults` hex values;
 * branded rooms swap in `--brand-primary|secondary|tertiary`.
 */
export function makeGameTheme(defaults: GameThemeDefaults) {
  const secondary = defaults.secondary ?? defaults.primary;
  const secondaryRgb = defaults.secondaryRgb ?? defaults.primaryRgb;
  const tertiary = defaults.tertiary ?? secondary;
  const tertiaryRgb = defaults.tertiaryRgb ?? secondaryRgb;

  return {
    primary: `var(--brand-primary, ${defaults.primary})`,
    primaryLight: `rgba(var(--brand-primary-rgb, ${defaults.primaryRgb}), 0.15)`,
    primaryBorder: `rgba(var(--brand-primary-rgb, ${defaults.primaryRgb}), 0.3)`,
    primaryStrong: `rgba(var(--brand-primary-rgb, ${defaults.primaryRgb}), 0.5)`,

    secondary: `var(--brand-secondary, ${secondary})`,
    secondaryLight: `rgba(var(--brand-secondary-rgb, ${secondaryRgb}), 0.15)`,
    secondaryBorder: `rgba(var(--brand-secondary-rgb, ${secondaryRgb}), 0.3)`,

    tertiary: `var(--brand-tertiary, ${tertiary})`,
    tertiaryLight: `rgba(var(--brand-tertiary-rgb, ${tertiaryRgb}), 0.15)`,
    tertiaryBorder: `rgba(var(--brand-tertiary-rgb, ${tertiaryRgb}), 0.3)`,

    accent: `var(--brand-tertiary, ${tertiary})`,
    accentLight: `rgba(var(--brand-tertiary-rgb, ${tertiaryRgb}), 0.15)`,
    accentBorder: `rgba(var(--brand-tertiary-rgb, ${tertiaryRgb}), 0.3)`,

    success: cssTheme.success,
    successLight: cssTheme.successLight,
    successBorder: cssTheme.successBorder,
    error: cssTheme.error,
    errorLight: cssTheme.errorLight,
    errorBorder: cssTheme.errorBorder,
    warning: cssTheme.warning,
    warningLight: cssTheme.warningLight,
    warningBorder: cssTheme.warningBorder,
    textMuted: cssTheme.textMuted,
  } as const;
}

/** Drinking / AreYouDrunk / KinkyOrNot / GameEnd — classic party pink + amber */
export const partyPinkGameTheme = makeGameTheme({
  primary: '#EC4899',
  primaryRgb: '236, 72, 153',
  secondary: '#FFEE58',
  secondaryRgb: '255, 238, 88',
  tertiary: '#F59E0B',
  tertiaryRgb: '245, 158, 11',
});

/** Party host chrome shared by pink party games */
export const partyHostCssTheme = {
  primary: partyPinkGameTheme.primary,
  primaryLight: partyPinkGameTheme.primaryLight,
  primaryBorder: partyPinkGameTheme.primaryBorder,
  accent: partyPinkGameTheme.accent,
  accentLight: partyPinkGameTheme.accentLight,
  accentBorder: partyPinkGameTheme.accentBorder,
} as const;

export const stonedYetGameTheme = makeGameTheme({
  primary: '#10B981',
  primaryRgb: '16, 185, 129',
  secondary: '#FFEE58',
  secondaryRgb: '255, 238, 88',
  tertiary: '#F59E0B',
  tertiaryRgb: '245, 158, 11',
});

export const hotTakesGameTheme = {
  ...makeGameTheme({
    primary: '#FB923C',
    primaryRgb: '249, 115, 22',
    secondary: '#FFEE58',
    secondaryRgb: '255, 238, 88',
    tertiary: '#F59E0B',
    tertiaryRgb: '245, 158, 11',
  }),
  /** Disagree — semantic, not brand tertiary */
  accent: '#F87171',
  accentLight: 'rgba(248, 113, 113, 0.28)',
  accentBorder: 'rgba(248, 113, 113, 0.65)',
  agree: '#4ADE80',
  agreeLight: 'rgba(74, 222, 128, 0.28)',
  agreeBorder: 'rgba(74, 222, 128, 0.65)',
  textMuted: 'rgba(255, 255, 255, 0.92)',
} as const;

export const wouldYouRatherHostTheme = {
  ...makeGameTheme({
    primary: '#A855F7',
    primaryRgb: '168, 85, 247',
    secondary: '#FFEE58',
    secondaryRgb: '255, 238, 88',
    tertiary: '#EC4899',
    tertiaryRgb: '236, 72, 153',
  }),
  optionA: '#22C55E',
  optionALight: 'rgba(34, 197, 94, 0.15)',
  optionABorder: 'rgba(34, 197, 94, 0.3)',
  optionB: '#EF4444',
  optionBLight: 'rgba(239, 68, 68, 0.15)',
  optionBBorder: 'rgba(239, 68, 68, 0.3)',
} as const;

export const wouldYouRatherPlayerTheme = {
  ...makeGameTheme({
    primary: '#C084FC',
    primaryRgb: '192, 132, 252',
    secondary: '#FFEE58',
    secondaryRgb: '255, 238, 88',
    tertiary: '#F472B6',
    tertiaryRgb: '244, 114, 182',
  }),
  optionA: '#4ADE80',
  optionALight: 'rgba(74, 222, 128, 0.28)',
  optionABorder: 'rgba(74, 222, 128, 0.65)',
  optionB: '#FBBF24',
  optionBLight: 'rgba(251, 191, 36, 0.28)',
  optionBBorder: 'rgba(251, 191, 36, 0.65)',
  textMuted: 'rgba(255, 255, 255, 0.92)',
} as const;

/** Close or Not / How Many — orange primary; mint = semantic “good” feedback */
export const closeOrNotGameTheme = {
  ...makeGameTheme({
    primary: '#F97316',
    primaryRgb: '249, 115, 22',
    secondary: '#FFE566',
    secondaryRgb: '255, 229, 102',
    tertiary: '#F59E0B',
    tertiaryRgb: '245, 158, 11',
  }),
  accent: '#34D399',
  accentLight: 'rgba(52, 211, 153, 0.15)',
  accentBorder: 'rgba(52, 211, 153, 0.3)',
} as const;

/** Standings / live-answer sidebar — follows pack (and branding) tokens */
export const standingsCssTheme = {
  primary: cssTheme.primary,
  primaryLight: cssTheme.primaryLight,
  accent: cssTheme.accent,
} as const;
